import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { Trash2, ArrowLeft } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PermissionButton } from "@/components/PermissionButton";
import { PermissionSelect } from "@/components/PermissionSelect";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { User } from 'lucide-react';
import { Checkbox } from "@/components/ui/checkbox";
import { LogoLoader } from '@/components/ui/logo-loader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useAlert } from '@/hooks/use-alert';
import { SeatUpgradePrompt } from '@/components/SeatUpgradePrompt';
import { PhoneInput } from "@/components/ui/phone-input";
import { IconButtonCancel, IconButtonSave } from "@/components/ui/icon-buttons";
import { getDefaultCountry } from '@/utils/country-detection';
import { ConicGradientBorder } from '@/components/conic-gradient-border';
import { normalizeSalutationCode } from '@/utils/salutation';

type OrgInfo = {
  id: string;
  name?: string;
  activeMembers?: number;
  maxUsers?: number | null;
  language?: string;
};

type Member = {
  user_id?: string;
  userId?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  mobile?: string;
  email: string;
  roleId?: string | null;
  roleName?: string | null;
  role?: string;
  status?: string;
  profilePhotoUrl?: string;
};

type SubscriptionUsage = {
  teamMemberCount: number;
  maxTeamMembers: number;
};

type SubscriptionInfo = {
  pricePerSeat?: number;
  billingInterval?: 'monthly' | 'quarterly' | 'yearly';
  currency?: string;
  planName?: string;
};

type MembersTabProps = {
  orgInfo: OrgInfo | null;
  orgId: string | null;
  currentUserRoleLabel: string;
  subscriptionUsage: SubscriptionUsage | null;
  subscriptionInfo?: SubscriptionInfo | null;
  roles: Array<any>;
  inviteRole: string;
  setInviteRole: (v: string) => void;
  members: Member[];
  membersLoading?: boolean;
  hasPermission: (perm: string) => boolean;
  inviteMember: (args: { orgId: string; email: string; role: string; firstName?: string; lastName?: string; mobile?: string; anrede?: string }) => void;
  inviteMemberLoading?: boolean;
  updateMemberRole: (args: { orgId: string; userId: string; roleId: string }) => void;
  removeMember: (args: { orgId: string; userId: string }) => void;
  removeMemberLoading?: boolean;
  removeInvitation: (args: { orgId: string; email: string }) => void;
  removeInvitationLoading?: boolean;
  inviteSentOpen?: boolean;
  inviteSentEmail?: string | null;
  onCloseInviteSent?: () => void;
  inviteMemberOpen: boolean;
  setInviteMemberOpen: (open: boolean) => void;
};

export function MembersTab({
  orgInfo,
  orgId,
  currentUserRoleLabel,
  subscriptionUsage,
  subscriptionInfo,
  roles,
  inviteRole,
  setInviteRole,
  members,
  membersLoading = false,
  hasPermission,
  inviteMember,
  inviteMemberLoading = false,
  updateMemberRole,
  removeMember,
  removeMemberLoading = false,
  removeInvitation,
  removeInvitationLoading = false,
  inviteSentOpen,
  inviteSentEmail,
  onCloseInviteSent,
  inviteMemberOpen,
  setInviteMemberOpen,
}: MembersTabProps) {
  const { t, i18n } = useTranslation(['pages.settings', 'common']);
  const { confirm } = useAlert();
  const { user: currentUser } = useCurrentUser();
  const isContractor = (currentUser as any)?.orgRole === 'contractor';
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteAnrede, setInviteAnrede] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteMobile, setInviteMobile] = useState("");
  const [emailSuggestions, setEmailSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [showSeatUpgrade, setShowSeatUpgrade] = useState(false);
  const [pendingInvite, setPendingInvite] = useState<{ email: string; role: string; firstName?: string; lastName?: string; mobile?: string; anrede?: string } | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const defaultCountry = getDefaultCountry();
  const isDeleting = removeMemberLoading || removeInvitationLoading;
  const hasManageMembersAccess =
    hasPermission('manage_members') || hasPermission('manage_organization');

  const getMemberName = useCallback((member: Member): string => {
    const snakeCaseName = `${member.first_name || ''} ${member.last_name || ''}`.trim();
    if (snakeCaseName) return snakeCaseName;
    const camelCaseName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
    if (camelCaseName) return camelCaseName;
    if (member.display_name?.trim()) return member.display_name.trim();
    return '-';
  }, []);

  const resolveMemberRoleName = useCallback((member: Member): string => {
    const explicitRoleName = member.roleName || member.role;
    if (explicitRoleName) return explicitRoleName;
    if (member.roleId) {
      const roleById = roles.find((r: any) => r.id === member.roleId);
      if (roleById?.name) return roleById.name;
    }
    return 'admin';
  }, [roles]);

  const resolveMemberRoleId = useCallback((member: Member): string | null => {
    if (member.roleId) return member.roleId;
    const roleName = member.roleName || member.role;
    if (!roleName) return null;
    const matchedRole = roles.find(
      (role: any) => (role.name || '').toLowerCase() === roleName.toLowerCase()
    );
    return matchedRole?.id || null;
  }, [roles]);

  // Get translated display name for a role from API-provided translations
  const getRoleDisplayName = useCallback((roleName: string): string => {
    const languageCode = i18n.language.split('-')[0]; // e.g., 'en-US' -> 'en'
    
    // Find the role object to get translations
    const role = roles.find((r: any) => r.name.toLowerCase() === roleName.toLowerCase());
    if (role?.translations && role.translations[languageCode]) {
      return role.translations[languageCode];
    }
    
    // Preserve custom role name casing exactly as stored.
    return role?.name || roleName;
  }, [roles, i18n.language]);

  useEffect(() => {
    let timer: number | undefined;
    const run = async () => {
      try {
        const res = await apiRequest(`/api/contractors/search?email=${encodeURIComponent(inviteEmail)}`);
        const data = await res.json();
        setEmailSuggestions(data?.data || []);
      } catch {
        setEmailSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    };
    if (inviteEmail && inviteEmail.length >= 2) {
      setIsSearching(true);
      timer = window.setTimeout(run, 300);
    } else {
      setEmailSuggestions([]);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [inviteEmail]);

  useEffect(() => {
    if (inviteSentOpen) {
      setEmailSuggestions([]);
      setInviteEmail("");
      setInviteMemberOpen(false);
    }
  }, [inviteSentOpen, setInviteMemberOpen]);

  const getMemberSelectionId = (member: Member) => member.user_id ?? member.userId ?? member.email;
  const isOwner = (member: Member) =>
    (((orgInfo as any)?.ownerId && (member.user_id ?? member.userId) === (orgInfo as any).ownerId) ||
      (!((orgInfo as any)?.ownerId) && resolveMemberRoleName(member).toLowerCase() === 'owner'));
  const isPendingInvitation = (member: Member) =>
    !(member.user_id ?? member.userId) && !!member.email && (member.status || '').toLowerCase() === 'invited';
  const canDeleteMember = (member: Member) =>
    hasManageMembersAccess && (
      (!!(member.user_id ?? member.userId) && !isOwner(member)) || isPendingInvitation(member)
    );
  const selectableMemberIds = (members || []).filter(canDeleteMember).map(getMemberSelectionId);
  const allSelected = selectableMemberIds.length > 0 && selectableMemberIds.every((id) => selectedMemberIds.includes(id));
  const someSelected = selectedMemberIds.length > 0 && !allSelected;

  const handleBulkDeleteMembers = async () => {
    if (selectedMemberIds.length === 0) return;
    const confirmed = await confirm({
      message: t('pages.settings:sections.members.deleteConfirm', { count: selectedMemberIds.length }),
      variant: 'destructive',
      confirmText: t('common:alerts.delete'),
    });
    if (!confirmed || !orgId) return;

    selectedMemberIds.forEach((id) => {
      const member = members.find((m) => getMemberSelectionId(m) === id);
      if (!member || !canDeleteMember(member)) return;

      if (member.user_id ?? member.userId) {
        removeMember({ orgId, userId: member.user_id ?? member.userId! });
      } else if (member.email) {
        removeInvitation({ orgId, email: member.email });
      }
    });
    setSelectedMemberIds([]);
  };

  const hasReachedLimit = subscriptionUsage && subscriptionUsage.teamMemberCount >= subscriptionUsage.maxTeamMembers;

  const isInviteFormValid = !!orgId && !!inviteEmail.trim() && !!inviteFirstName.trim() && !!inviteLastName.trim() && !inviteMemberLoading;

  const resetInviteForm = useCallback(() => {
    setInviteEmail("");
    setInviteAnrede("");
    setInviteFirstName("");
    setInviteLastName("");
    setInviteMobile("");
    setEmailSuggestions([]);
  }, []);

  // Handle invite action - check seat limit first
  const handleInviteClick = () => {
    const email = inviteEmail.trim();
    const role = inviteRole || 'admin';
    const firstName = inviteFirstName.trim() || undefined;
    const lastName = inviteLastName.trim() || undefined;
    const mobile = inviteMobile.trim() || undefined;
    const anrede = normalizeSalutationCode(inviteAnrede) || undefined;
    if (!orgId || !email || inviteMemberLoading) return;
    if (!firstName || !lastName) return;

    const payload = { orgId, email, role, firstName, lastName, mobile, anrede };

    if (hasReachedLimit) {
      setPendingInvite(payload);
      setShowSeatUpgrade(true);
    } else {
      inviteMember(payload);
      resetInviteForm();
    }
  };

  const handleCloseInviteModal = useCallback(() => {
    setInviteMemberOpen(false);
    setPendingInvite(null);
    setShowSeatUpgrade(false);
    resetInviteForm();
  }, [resetInviteForm, setInviteMemberOpen]);

  // Handle successful seat purchase - retry the pending invite
  const handleSeatUpgradeSuccess = () => {
    if (pendingInvite && orgId) {
      inviteMember({
        orgId,
        email: pendingInvite.email,
        role: pendingInvite.role,
        firstName: pendingInvite.firstName,
        lastName: pendingInvite.lastName,
        mobile: pendingInvite.mobile,
        anrede: pendingInvite.anrede,
      });
      resetInviteForm();
      setInviteMemberOpen(false);
      setPendingInvite(null);
    }
    setShowSeatUpgrade(false);
  };

  return (
    <div>
      <div className="space-y-3">
        {/* Members list */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xs">
              {t('pages.settings:sections.members.title')}
              <span className="ml-2 text-[11px] font-normal text-[#666666]">
                {t('pages.settings:sections.members.description')}
              </span>
            </CardTitle>
            <PermissionButton
              className="bg-white hover:bg-white"
              variant="pillOutline"
              size="sm"
              permission={['manage_members', 'manage_organization']}
              fallbackText={t('pages.settings:sections.members.deletePermission') || ''}
              disabled={selectedMemberIds.length === 0 || isDeleting}
              onClick={handleBulkDeleteMembers}
            >
              {isDeleting ? (
                <>
                  <LogoLoader size="sm" className="mr-1" />
                  {t('common:loading')}
                </>
              ) : (
                <>
                  <Trash2 className="h-3 w-3 mr-1" />
                  {t('pages.settings:sections.members.deleteSelected')}
                </>
              )}
            </PermissionButton>
          </CardHeader>
          <CardContent>
            <div className="w-full overflow-hidden">
              <div className="overflow-x-auto overflow-y-auto">
                <table className="min-w-full listen-table">
                  <thead className="sticky top-0 z-10">
                    <tr className="table-header-row">
                      <th className="table-header-cell w-10">
                        <Checkbox
                          checked={allSelected ? true : someSelected ? "indeterminate" : false}
                          onCheckedChange={() => {
                            if (allSelected) {
                              setSelectedMemberIds([]);
                              return;
                            }
                            setSelectedMemberIds(selectableMemberIds);
                          }}
                          aria-label={t('table.headers.selectAll')}
                        />
                      </th>
                      <th className="table-header-cell w-40">
                        <span>{t('pages.settings:sections.members.table.name')}</span>
                      </th>
                      <th className="table-header-cell w-40">
                        <span>{t('pages.settings:sections.members.table.lastName')}</span>
                      </th>
                      <th className="table-header-cell w-36">
                        <span>{t('pages.settings:sections.members.table.mobile')}</span>
                      </th>
                      <th className="table-header-cell flex-1">
                        <span>{t('pages.settings:sections.members.table.email')}</span>
                      </th>
                      <th className="table-header-cell w-40">
                        <span>{t('pages.settings:sections.members.table.role')}</span>
                      </th>
                      <th className="table-header-cell w-24">
                        <span>{t('pages.settings:sections.members.table.status')}</span>
                      </th>
                    </tr>
                  </thead>
                <tbody>
                  {membersLoading ? (
                    <tr className="table-body-row">
                      <td colSpan={7} className="table-body-cell text-center py-8">
                        <div className="flex items-center justify-center">
                          <LogoLoader size="md" text={t('common:loading')} showText />
                        </div>
                      </td>
                    </tr>
                  ) : members?.length ? members.map((m: any) => (
                    <tr
                      key={(m.user_id ?? m.userId) ? `u:${m.user_id ?? m.userId}` : `e:${(m.email || '').toLowerCase()}`}
                      className="table-body-row cursor-pointer hover:bg-slate-50/80"
                      onClick={() => setSelectedMember(m)}
                    >
                      <td className="table-body-cell" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedMemberIds.includes(getMemberSelectionId(m))}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedMemberIds((prev) => [...prev, getMemberSelectionId(m)]);
                            } else {
                              setSelectedMemberIds((prev) => prev.filter((id) => id !== getMemberSelectionId(m)));
                            }
                          }}
                          disabled={!canDeleteMember(m)}
                          aria-label={t('table.headers.selectRow')}
                        />
                      </td>
                      <td className="table-body-cell">{m.firstName ?? m.first_name ?? '-'}</td>
                      <td className="table-body-cell">{m.lastName ?? m.last_name ?? '-'}</td>
                      <td className="table-body-cell">{m.mobile ? String(m.mobile) : '-'}</td>
                      <td className="table-body-cell">{m.email}</td>
                      <td className="table-body-cell" onClick={(e) => e.stopPropagation()}>
                        {(m.status || '').toLowerCase() === 'invited' ? (
                          <span className="text-slate-600">{getRoleDisplayName(resolveMemberRoleName(m))}</span>
                        ) : (
                          isOwner(m) ? (
                            <span className="text-slate-600">{getRoleDisplayName(resolveMemberRoleName(m))}</span>
                          ) : (
                            resolveMemberRoleId(m) ? (
                            <PermissionSelect 
                              defaultValue={resolveMemberRoleId(m)!}
                              permission={['manage_members', 'manage_organization']}
                              fallbackText="You don't have permission to edit member roles"
                              onValueChange={(roleId) => {
                                if (!orgId || !(m.user_id ?? m.userId)) return;
                                updateMemberRole({ orgId, userId: m.user_id ?? m.userId!, roleId });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.map((role: any) => (
                                  <SelectItem key={role.id} value={role.id}>
                                    {getRoleDisplayName(role.name)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </PermissionSelect>
                            ) : (
                              <span className="text-slate-600">{getRoleDisplayName(resolveMemberRoleName(m))}</span>
                            )
                          )
                        )}
                      </td>
                      <td className="table-body-cell">
                        {(m.user_id ?? m.userId) ? (
                          <span className="text-slate-500">-</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800">
                            {t('pages.settings:sections.members.status.invited')}
                          </span>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr className="table-body-row">
                      <td colSpan={7} className="table-body-cell text-center">{t('pages.settings:sections.members.table.empty')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </CardContent>
        </Card>

        {orgInfo && !isContractor && (
          <div className="flex justify-end text-xs mt-4">
            Seats: <span className="font-medium ml-1">{orgInfo.activeMembers}</span>
            {orgInfo.maxUsers ? (
              <span> / {orgInfo.maxUsers}</span>
            ) : (
              <span> / ∞</span>
            )}
          </div>
        )}

        {/* Invite success modal */}
        <Dialog open={!!inviteSentOpen} onOpenChange={(open) => { if (!open && onCloseInviteSent) onCloseInviteSent(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invitation sent</DialogTitle>
              <DialogDescription>
                {inviteSentEmail ? `An invitation was sent to ${inviteSentEmail}.` : 'Invitation was sent successfully.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="pillOutline" className="bg-white hover:bg-white" onClick={onCloseInviteSent}>OK</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Member detail modal - contractors-style */}
        <Dialog open={selectedMember !== null} onOpenChange={(open) => { if (!open) setSelectedMember(null); }}>
          <DialogContent className="ticket-modal-shell [&>button]:hidden" hideClose>
            {selectedMember && (
              <>
                <div className="ticket-modal-header">
                  <div className="ticket-modal-title-row">
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => setSelectedMember(null)}
                      className="h-8 w-8 p-0 text-[#333333] hover:text-[#333333] hover:bg-gray-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <DialogTitle className="ticket-modal-title">
                      {getMemberName(selectedMember)}
                    </DialogTitle>
                  </div>
                  <div className="ticket-modal-actions">
                    <IconButtonCancel
                      onClick={() => setSelectedMember(null)}
                      size={30}
                      title={t('common:alerts.cancel')}
                    />
                  </div>
                </div>
                <DialogDescription className="sr-only">
                  {t('pages.settings:sections.members.detailTitle')}
                </DialogDescription>

                <div className="ticket-modal-scroll">
                  <div className="ticket-modal-body">
                    <div className="card-container card-container-muted card-container-no-shadow p-[32px]">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          {(selectedMember.user_id ?? selectedMember.userId) && (
                            <div className="flex justify-center">
                              <ConicGradientBorder size={80} borderWidth={4}>
                                {selectedMember.profilePhotoUrl ? (
                                  <img
                                    src={selectedMember.profilePhotoUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-full h-full bg-slate-200 flex items-center justify-center text-slate-600 text-lg font-semibold">
                                    {(selectedMember.firstName ?? selectedMember.first_name ?? '').charAt(0).toUpperCase()}
                                    {(selectedMember.lastName ?? selectedMember.last_name ?? '').charAt(0).toUpperCase() || '?'}
                                  </div>
                                )}
                              </ConicGradientBorder>
                            </div>
                          )}
                          <CornerLabelField label={t('pages.settings:sections.members.table.name')}>
                            <div className="textbox-readonly min-h-8 flex items-center">
                              {selectedMember.firstName ?? selectedMember.first_name ?? '-'}
                            </div>
                          </CornerLabelField>
                          <CornerLabelField label={t('pages.settings:sections.members.table.lastName')}>
                            <div className="textbox-readonly min-h-8 flex items-center">
                              {selectedMember.lastName ?? selectedMember.last_name ?? '-'}
                            </div>
                          </CornerLabelField>
                          <CornerLabelField label={t('pages.settings:sections.members.table.mobile')}>
                            <div className="textbox-readonly min-h-8 flex items-center">
                              {selectedMember.mobile ? String(selectedMember.mobile) : '-'}
                            </div>
                          </CornerLabelField>
                          <CornerLabelField label={t('pages.settings:sections.members.table.email')}>
                            <div className="textbox-readonly min-h-8 flex items-center">
                              {selectedMember.email}
                            </div>
                          </CornerLabelField>
                          <CornerLabelField label={t('pages.settings:sections.members.table.role')}>
                            {isOwner(selectedMember) || (selectedMember.status || '').toLowerCase() === 'invited' ? (
                              <div className="textbox-readonly min-h-8 flex items-center">
                                {getRoleDisplayName(resolveMemberRoleName(selectedMember))}
                              </div>
                            ) : (
                              resolveMemberRoleId(selectedMember) ? (
                              <PermissionSelect
                                defaultValue={resolveMemberRoleId(selectedMember)!}
                                permission={['manage_members', 'manage_organization']}
                                fallbackText="You don't have permission to edit member roles"
                                onValueChange={(roleId) => {
                                  if (!orgId || !(selectedMember.user_id ?? selectedMember.userId)) return;
                                  updateMemberRole({ orgId, userId: selectedMember.user_id ?? selectedMember.userId!, roleId });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.map((role: any) => (
                                    <SelectItem key={role.id} value={role.id}>
                                      {getRoleDisplayName(role.name)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </PermissionSelect>
                              ) : (
                                <div className="textbox-readonly min-h-8 flex items-center">
                                  {getRoleDisplayName(resolveMemberRoleName(selectedMember))}
                                </div>
                              )
                            )}
                          </CornerLabelField>
                          <CornerLabelField label={t('pages.settings:sections.members.table.status')}>
                            <div className="min-h-8 flex items-center">
                              {(selectedMember.user_id ?? selectedMember.userId) ? (
                                <span className="text-slate-500">-</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800">
                                  {t('pages.settings:sections.members.status.invited')}
                                </span>
                              )}
                            </div>
                          </CornerLabelField>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Invite member modal - contractors-style */}
        <Dialog open={inviteMemberOpen} onOpenChange={(open) => { if (!open) handleCloseInviteModal(); }}>
          <DialogContent className="ticket-modal-shell [&>button]:hidden" hideClose>
            <div className="ticket-modal-header">
              <div className="ticket-modal-title-row">
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={handleCloseInviteModal}
                  className="h-8 w-8 p-0 text-[#333333] hover:text-[#333333] hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <DialogTitle className="ticket-modal-title">
                  {t('pages.settings:sections.invite.title')}
                </DialogTitle>
              </div>
              <div className="ticket-modal-actions">
                <IconButtonCancel
                  onClick={handleCloseInviteModal}
                  size={30}
                  title={t('common:alerts.cancel')}
                />
                <IconButtonSave
                  onClick={handleInviteClick}
                  disabled={!isInviteFormValid || inviteMemberLoading}
                  size={30}
                  title={inviteMemberLoading ? t('common:loading') : t('pages.settings:sections.members.invite.sendInvite', { defaultValue: 'Send invite' })}
                />
              </div>
            </div>
            <DialogDescription className="sr-only">
              {t('pages.settings:sections.invite.title')}
            </DialogDescription>

            <div className="ticket-modal-scroll">
              <div className="ticket-modal-body">
                <div className="card-container card-container-muted card-container-no-shadow p-[32px]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <CornerLabelField label={t('pages.settings:sections.members.invite.salutation')}>
                            <Select value={inviteAnrede} onValueChange={(value) => setInviteAnrede(normalizeSalutationCode(value) || "")}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('pages.settings:sections.members.invite.salutationPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="MR">{t('pages.settings:sections.members.invite.salutationMr')}</SelectItem>
                                <SelectItem value="MRS">{t('pages.settings:sections.members.invite.salutationMrs')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </CornerLabelField>
                        </div>
                        <div className="col-span-2">
                          <CornerLabelField label={`${t('pages.settings:sections.members.invite.name')} *`}>
                            <Input
                              id="inviteFirstName"
                              value={inviteFirstName}
                              onChange={(e) => setInviteFirstName(e.target.value)}
                              placeholder={t('pages.settings:sections.members.invite.namePlaceholder')}
                              disabled={!hasManageMembersAccess}
                            />
                          </CornerLabelField>
                        </div>
                      </div>
                      <div>
                        <CornerLabelField label={`${t('pages.settings:sections.members.invite.lastName')} *`}>
                          <Input
                            id="inviteLastName"
                            value={inviteLastName}
                            onChange={(e) => setInviteLastName(e.target.value)}
                            placeholder={t('pages.settings:sections.members.invite.lastNamePlaceholder')}
                            disabled={!hasManageMembersAccess}
                          />
                        </CornerLabelField>
                      </div>
                      <div>
                        <CornerLabelField label={t('pages.settings:sections.members.invite.telephone')}>
                          <PhoneInput
                            id="inviteMobile"
                            value={inviteMobile}
                            onChange={(value) => setInviteMobile(value)}
                            defaultCountry={defaultCountry}
                            allowInvalid
                            disabled={!hasManageMembersAccess}
                          />
                        </CornerLabelField>
                      </div>
                      <div>
                        <CornerLabelField label={`${t('pages.settings:sections.members.invite.emailLabel')} *`}>
                          <div className="relative">
                            <Input
                              id="inviteEmail"
                              type="email"
                              placeholder="email@example.com"
                              disabled={!hasManageMembersAccess}
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                            />
                            {isSearching && (
                              <LogoLoader size="sm" className="absolute right-2 top-2" />
                            )}
                          </div>
                          {emailSuggestions.length > 0 && (
                            <div className="mt-1 border border-gray-200 rounded-md max-h-40 overflow-y-auto bg-white shadow-sm z-10">
                              {emailSuggestions.map((u) => (
                                <button
                                  key={u.id}
                                  onClick={() => {
                                    setInviteEmail(u.email);
                                    setInviteFirstName(u.first_name ?? '');
                                    setInviteLastName(u.last_name ?? '');
                                    setInviteMobile(u.mobile ?? '');
                                    setEmailSuggestions([]);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
                                  type="button"
                                >
                                  <User className="h-4 w-4 text-gray-400" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim()}</div>
                                    <div className="text-xs text-gray-500 truncate">{u.email}</div>
                                    {u.position && (
                                      <div className="text-xs text-gray-400">{u.position}</div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </CornerLabelField>
                      </div>
                      <div>
                        <CornerLabelField label={t('pages.settings:sections.members.invite.roleLabel')}>
                          <PermissionSelect
                            value={inviteRole}
                            permission={['manage_members', 'manage_organization']}
                            fallbackText="You don't have permission to invite members"
                            onValueChange={(val) => setInviteRole(val)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t('pages.settings:sections.members.invite.roleLabel')} />
                            </SelectTrigger>
                            <SelectContent>
                              {roles?.map((role: any) => (
                                <SelectItem key={role.id} value={role.name}>
                                  {getRoleDisplayName(role.name)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </PermissionSelect>
                        </CornerLabelField>
                      </div>
                    </div>
                  </div>
                </div>
                {hasReachedLimit && orgInfo && subscriptionUsage && (
                  <div className="mt-4">
                    <SeatUpgradePrompt
                      orgId={orgId}
                      currentSeats={orgInfo.maxUsers || subscriptionUsage.maxTeamMembers}
                      maxTeamMembers={subscriptionUsage.maxTeamMembers}
                      activeMembers={subscriptionUsage.teamMemberCount}
                      pricePerSeat={subscriptionInfo?.pricePerSeat}
                      currency={subscriptionInfo?.currency}
                      onSuccess={handleSeatUpgradeSuccess}
                      variant="inline"
                      showTrigger={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Standalone Seat Upgrade Prompt Dialog */}
        {showSeatUpgrade && orgInfo && subscriptionUsage && (
          <SeatUpgradePrompt
            orgId={orgId}
            currentSeats={orgInfo.maxUsers || subscriptionUsage.maxTeamMembers}
            maxTeamMembers={subscriptionUsage.maxTeamMembers}
            activeMembers={subscriptionUsage.teamMemberCount}
            pricePerSeat={subscriptionInfo?.pricePerSeat}
            currency={subscriptionInfo?.currency}
            onSuccess={handleSeatUpgradeSuccess}
            variant="banner"
            showTrigger={false}
          />
        )}
      </div>
    </div>
  );
}

export default MembersTab;
